/**
 * The window that hands a task to somebody — one, wherever it is opened from.
 *
 * It is opened from three places: the Assign button on the task page, the name
 * beside "Assigned to" in that page's hero, and the Who chip on a board card. They
 * were three `ActionDialog`s with the same five props written out three times, and
 * the day the hand-over learnt to say what it is made from (`if_was`), one of the
 * three was given it and the other two went on taking a task off whoever had picked
 * it up since the card was drawn — which an open window can make arbitrarily long
 * ago, because it holds the page's reads still. A caller chooses what opens it and
 * how big that is; nothing else is a caller's to choose.
 *
 * **Made from who holds it and where it stands, as the caller's task says.** The API
 * compares that under the task's own lock (`apps/api` `tasks/service.ts`
 * `assertStillWas`) and refuses a hand-over made from an old copy; `ActionForm` says
 * so at the foot of the window, and a second press is the reader's decision to go
 * ahead anyway (`insist` — which no move's window may: routing a task is a decision
 * about it whoever holds it, where a move is offered only on the reader's own).
 * `tasks_assign`'s one box answers two of its fields, so the form
 * cannot work the condition out for itself the way it does for `tasks_update` — it
 * is this file's to send, through `fixed`.
 */

import type { ReactNode } from 'react'

import { taskCanBeUnassigned, type TaskView } from '@fareast/shared'

import { Muted, Small } from '../portal/Text.js'
import { ActionDialog } from './ActionDialog.js'
import { confirmUnassign, NOBODY } from './ActionField.js'
import { moveWindowOf } from './taskWords.js'

export type AssignDialogProps = {
  /** The task as the page showing the trigger has it. */
  readonly task: Pick<TaskView, 'ref' | 'status' | 'assignee_kind' | 'assignee_id' | 'updated_at'>
  /** What opens the window: a button, a name, a chip. */
  readonly trigger: ReactNode
  readonly size?: 'sm' | undefined
}

/** What the picker leaves out on a task that stays with somebody: the Who cell's rule. */
const STAYS_HELD = { assignee: [NOBODY] } as const

export function AssignDialog({ task, trigger, size }: AssignDialogProps) {
  return (
    <ActionDialog
      action="tasks_assign"
      label="Assign"
      title="Hand it to somebody"
      submitLabel="Assign"
      {...moveWindowOf(task)}
      insist
      without={taskCanBeUnassigned(task.status) ? undefined : STAYS_HELD}
      confirm={confirmUnassign([task])}
      size={size}
      trigger={trigger}
    >
      {/*
        "Unassigned" is not offered on a task that cannot be left with nobody (`without`),
        and a list with a choice missing owes the reader the reason.
      */}
      {taskCanBeUnassigned(task.status) ? null : (
        <Small>
          <Muted>
            A blocked task stays with somebody — it is waiting, and the wait is theirs. It can be
            given to somebody else here; to take everybody off it, unblock or cancel it first.
          </Muted>
        </Small>
      )}
    </ActionDialog>
  )
}
